import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { useScaleEvents } from '@components/ArtBoard/Canvas/hooks/events'
import {
  Factory,
  type ID,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { thunkAddStickyNote } from '@store/migrationActions/thunkActions/thunkStickyNotesActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { type ArtBoardMode, artBoardActions } from '@store/reducers/artboard'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import { getId } from '@store/utils'
import {
  commitProjectRequest,
  setCursorGrabMode,
  setCursorSelectMode,
} from '@storeOld/actionCreators'
import {
  filterByTag,
  removeFilterByTag,
} from '@storeOld/reducers/canvas/canvas'
import { openModal, threadsPanelShow } from '@storeOld/reducers/ui'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtboardMode, useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import {
  FILE_ATTACHMENTS_PERMISSION,
  TAGS_PERMISSION,
  TOOLBAR_ICONS_PERMISSION,
} from '@/permissions/project/definitions'
import type { AppState } from '@/types'
import centerElement from '@/utils/centerElement'
import { useSeoModeSwitcher } from './useSeoModeSwitcher'

// Elements an external link must not land on top of when it is centred.
const LINK_OBSTACLES = ['.ExternalLink-container', '.StickyNote-container']

export const useProjectToolbar = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const {
    newThread,
    seoMode,
    estimate: estimateMode,
    gallery: imageMode,
  } = useArtboardMode()

  const cursorMode = useSelector<AppState, ArtBoardCursorMode>(
    ({ artboard }) => artboard.cursorMode
  )

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)

  const canComment = useSelector<AppState, boolean>(
    ({ manager: { meta }, auth }) =>
      meta.access.canComment && (meta.anonymous_comments || auth.authenticated)
  )

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)

  const selectedTags = useSelector<AppState, ID[]>(
    ({ canvas }) => canvas.tags.selectedTags
  )

  const imageModeOn = useSelector(migrationProjectSelector.getProjectImagesMode)

  const attachAccess = useProjectAccess(FILE_ATTACHMENTS_PERMISSION.ATTACH_FILE)
  const tagsAccess = useProjectAccess(TAGS_PERMISSION.SHOW)
  const { hasAccess: estimateAccess } = useProjectAccess(
    TOOLBAR_ICONS_PERMISSION.ESTIMATE
  )
  const { hasAccess: privateEstimateAccess } = useProjectAccess(
    TOOLBAR_ICONS_PERMISSION.ESTIMATE_PRIVATE
  )

  const privateEstimate = useSelector(
    migrationProjectSelector.getProjectEstimateSettingPrivate
  )

  const estimateAllowed =
    estimateAccess && (!privateEstimate || privateEstimateAccess)

  // The tri-state `settings.estimateMode`, resolved in one place by the
  // selector — see getProjectEstimateIconVisible.
  const estimateIconOn = useSelector(
    migrationProjectSelector.getProjectEstimateIconVisible
  )

  // The icon exists only for someone who has BOTH: a plan that allows
  // estimates, and the tri-state resolving to "visible". Free and trial
  // therefore never see it — not even on a project that already carries
  // estimate data after a downgrade from Pro, where the tri-state fallback on
  // its own would say "show".
  //
  // This knowingly contradicts OCT-3644, which asked for "if the estimator is
  // enabled, the icon is shown for all user types". The product owner resolved
  // that on 2026-08-27 in favour of "free never sees it". The contradiction is
  // settled, not overlooked — do not "restore" OCT-3644 here.
  //
  // `estimateAllowed` is read here and nowhere else on purpose: every state in
  // which it is false is already hidden, so a `!estimateAllowed` branch on the
  // button's `disabled` would be unreachable.
  const estimateHidden = !(estimateAllowed && estimateIconOn)

  // While the artboard is off — the assistant is working (OCT-4826), saving
  // is down, the activity history is open — the editing controls freeze.
  const artboardDisabled = useArtBoardDisabled()

  // A file attachment is on its way: the attach button shows a loader.
  // The upload saga keeps the file id among the loaders (also for collaborators
  // over the socket) and the attachment holds that id as its data.
  const attachUploading = useSelector<AppState, boolean>(
    ({ loaders, manager }) =>
      manager.project.attachments.some(
        (attachment) =>
          attachment.type === 'file' && loaders.includes(attachment.data.id)
      )
  )

  const { active: seoActive, onClick: onToggleSeoMode } = useSeoModeSwitcher()

  // SEO, estimate and image modes keep only the controls that make sense
  // outside editing: the mode exit, AI, the view toggles and the SEO switch.
  const modeLocked = seoMode || estimateMode || imageMode

  const { onResetZoom } = useScaleEvents()

  const onExitMode = () => {
    dispatch(artBoardActions.changeMode('edit'))
  }

  const onCursorMode = (mode: ArtBoardCursorMode) => {
    if (mode === 'grab') {
      dispatch(setCursorGrabMode())
      return
    }

    dispatch(setCursorSelectMode())
    sendGtmEvent(GtmTriggerName.MULTISELECT_MODE_ON)
  }

  const onAddStickyNote = () => {
    sendGtmEvent(GtmTriggerName.STICKY_NOTE_ADD, 'Click to add sticky note')
    dispatch(thunkAddStickyNote())
  }

  const onAddExternalLink = (url: string) => {
    sendGtmEvent(GtmTriggerName.EXTERNAL_LINK_ADD, 'Add external link')

    const offset = centerElement(0, 0, LINK_OBSTACLES)
    const link = Factory.externalLink({
      data: Factory.externalLinkData({ url }),
      id: getId(),
      offset,
    })

    dispatch(
      commitProjectRequest(
        ProjectActions.externalLinks.add({
          parents: [activeTab],
          index: 0,
          items: [link],
        })
      )
    )
    dispatch(
      ProjectActions.externalLinks.changeLoading({ id: link.id, loading: true })
    )
  }

  const onToggleComments = () => {
    // The second click on the active tool cancels the comment: the artboard
    // is back to itself, crosshair included. The panel stays open — closing
    // it cancels the comment the same way (leaveCommentModeOnPanelClose).
    if (newThread) {
      dispatch(artBoardActions.changeMode('edit'))
      return
    }

    dispatch(artBoardActions.changeMode('new_thread'))
    // Both panels live in the same right-hand slot (OCT-5034).
    dispatch(artBoardActions.showStructure({ showStructure: false }))
    dispatch(threadsPanelShow())
  }

  const onToggleEstimate = () => {
    const next: ArtBoardMode = estimateMode ? 'edit' : 'estimate'

    dispatch(artBoardActions.changeMode(next))
  }

  const onToggleImageMode = () => {
    const next: ArtBoardMode = imageMode ? 'edit' : 'gallery'

    dispatch(artBoardActions.changeMode(next))
    onResetZoom()
  }

  // Plan-gated controls stay clickable and sell the plan, like the page
  // toolbar does for frames and tags. Free plans hold these as NO rather
  // than NA, so the lock is the absence of access, not one of its flavours.
  const onUpgradeTags = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_TAGS)
    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
        modalProps: { slideType: 'tags' },
      })
    )
  }

  const onUpgradeAttachments = () => {
    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
      })
    )
  }

  const onToggleTagFilter = (id: ID) => {
    const action = selectedTags.includes(id)
      ? removeFilterByTag(id)
      : filterByTag(id)

    dispatch(action)
  }

  return {
    actions: {
      onAddExternalLink,
      onAddStickyNote,
      onCursorMode,
      onExitMode,
      onToggleComments,
      onToggleEstimate,
      onToggleImageMode,
      onToggleSeoMode,
      onToggleTagFilter,
      onUpgradeAttachments,
      onUpgradeTags,
    },
    aiDisabled: !canEdit,
    // The mockup keeps AI out of the estimate and image modes (figma 86165:420742).
    aiHidden: estimateMode || imageMode,
    attachDisabled: artboardDisabled,
    attachLocked: !attachAccess.hasAccess,
    attachUploading,
    canEdit,
    commentsActive: newThread,
    commentsDisabled: artboardDisabled || !canComment,
    cursorMode,
    estimateActive: estimateMode,
    estimateHidden,
    imageActive: imageMode,
    imageHidden: !imageModeOn,
    modeLocked,
    seoActive,
    tagsActive: selectedTags.length > 0,
    tagsDisabled: artboardDisabled,
    tagsLocked: !tagsAccess.hasAccess,
    toolsDisabled: artboardDisabled,
  }
}
